export type DatChoice = {
  label: string
  value: string
}

const baseUrl = (import.meta.env.VITE_AV_API_BASE_URL || '/av-api').replace(/\/$/, '')

const requestCache = new Map<string, Promise<DatChoice[]>>()

const fetchJson = async (url: string) => {
  if (requestCache.has(url)) {
    return requestCache.get(url)!
  }

  const promise = fetch(url, { headers: { Accept: 'application/json' } })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }
      return response.json()
    })
    .catch((err) => {
      requestCache.delete(url)
      throw err
    })

  requestCache.set(url, promise)
  return promise
}

const withParams = (path: string, params: Record<string, string | number | undefined>) => {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    query.append(key, String(value))
  })
  const suffix = query.toString()
  return `${baseUrl}${path}${suffix ? `?${suffix}` : ''}`
}

export const datConfigApi = {
  manufacturers: async (): Promise<DatChoice[]> =>
    fetchJson(withParams('/api/car-config-dat/manufacturers', {})),
  firstRegYears: async (
    manufacturer: string,
    maintype?: string
  ): Promise<DatChoice[]> =>
    fetchJson(
      withParams('/api/car-config-dat/first_reg_years', { manufacturer, maintype })
    ),
  maintypes: async (
    manufacturer: string,
    firstRegYear?: string | number
  ): Promise<DatChoice[]> =>
    fetchJson(
      withParams('/api/car-config-dat/maintypes', {
        manufacturer,
        first_reg_year: firstRegYear,
      })
    ),
  bodyDesigns: async (
    manufacturer: string,
    maintype: string,
    firstRegYear: string | number
  ): Promise<DatChoice[]> =>
    fetchJson(
      withParams('/api/car-config-dat/body_designs', {
        manufacturer,
        maintype,
        first_reg_year: firstRegYear,
      })
    ),
  fuelTypes: async (
    manufacturer: string,
    maintype: string,
    firstRegYear: string | number,
    bodyDesigns: string
  ): Promise<DatChoice[]> =>
    fetchJson(
      withParams('/api/car-config-dat/fuel_types', {
        manufacturer,
        maintype,
        first_reg_year: firstRegYear,
        body_designs: bodyDesigns,
      })
    ),
  gearboxTypes: async (
    manufacturer: string,
    maintype: string,
    firstRegYear: string | number,
    bodyDesigns: string,
    fuelTypes: string
  ): Promise<DatChoice[]> =>
    fetchJson(
      withParams('/api/car-config-dat/gearbox_types', {
        manufacturer,
        maintype,
        first_reg_year: firstRegYear,
        body_designs: bodyDesigns,
        fuel_types: fuelTypes,
      })
    ),
  enginePower: async (
    manufacturer: string,
    maintype: string,
    firstRegYear: string | number,
    bodyDesigns: string,
    fuelTypes: string,
    gearboxTypes: string
  ): Promise<DatChoice[]> =>
    fetchJson(
      withParams('/api/car-config-dat/engine_power', {
        manufacturer,
        maintype,
        first_reg_year: firstRegYear,
        body_designs: bodyDesigns,
        fuel_types: fuelTypes,
        gearbox_types: gearboxTypes,
      })
    ),
  subtypes: async (
    manufacturer: string,
    maintype: string,
    firstRegYear: string | number,
    bodyDesigns: string,
    fuelTypes: string,
    gearboxTypes: string,
    enginePower: string | number
  ): Promise<DatChoice[]> =>
    fetchJson(
      withParams('/api/car-config-dat/subtypes', {
        manufacturer,
        maintype,
        first_reg_year: firstRegYear,
        body_designs: bodyDesigns,
        fuel_types: fuelTypes,
        gearbox_types: gearboxTypes,
        engine_power: enginePower,
      })
    ),
}
